import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const navItems = [
    { label: t.nav.network, to: "/#how-it-works", id: "network" },
    { label: t.nav.brands, to: "/brands", id: "brands" },
    { label: t.nav.people, to: "/people", id: "people" },
    { label: t.nav.specs, to: "/#specs", id: "specs" },
    { label: t.nav.calculator, to: "/#calculator", id: "calculator" },
    { label: t.nav.about, to: "/about", id: "about" },
  ];

  return (
    <>
      {/* Top bar: network note + language */}
      <div className="border-b spec-border bg-white px-4 sm:px-6 py-2.5 text-xs font-mono-spec flex items-center justify-between gap-2 sm:gap-4">
        <span className="text-slate-600 flex items-center gap-3">
          <span className="inline-block w-2 h-2 rounded-full bg-gold-600" />
          <span data-testid="topbar-network">{t.topbar.network}</span>
        </span>
        <div className="flex items-center gap-4 text-slate-500">
          <button
            data-testid="lang-toggle-de"
            onClick={() => setLang("de")}
            className={lang === "de" ? "text-slate-900 font-semibold" : "hover:text-slate-900 cursor-pointer"}
          >
            DE
          </button>
          <button
            data-testid="lang-toggle-en"
            onClick={() => setLang("en")}
            className={lang === "en" ? "text-slate-900 font-semibold" : "hover:text-slate-900 cursor-pointer"}
          >
            EN
          </button>
        </div>
      </div>

      {/* Main navigation */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b spec-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="header-logo" className="flex items-center">
              <Logo markClass="w-6 h-6 text-blue-600" textClass="text-xl" />
            </Link>
            <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-600">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  data-testid={`nav-${item.id}`}
                  className="hover:text-slate-950 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/konto"
              data-testid="nav-account"
              className="text-sm font-medium text-slate-700 hover:text-slate-950 hidden sm:inline-block"
            >
              {t.nav.account}
            </Link>
            <Link
              to="/konto/login"
              data-testid="nav-login"
              className="text-sm font-medium text-slate-700 hover:text-slate-950 hidden sm:inline-block"
            >
              {t.nav.login}
            </Link>
            <Link
              to="/#contact"
              data-testid="nav-cta"
              className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 text-xs font-mono-spec font-semibold bg-slate-950 text-white rounded-full hover:bg-slate-800 transition-colors shadow-sm"
            >
              {t.nav.cta}
            </Link>
            <button
              data-testid="mobile-menu-button"
              className="lg:hidden text-slate-900"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden border-t spec-border bg-white px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.to}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => setOpen(false)}
                className="block text-sm font-medium text-slate-700 hover:text-slate-950 py-1"
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-2 flex items-center gap-4 border-t spec-border">
              <Link to="/konto" data-testid="mobile-nav-account" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-700">
                {t.nav.account}
              </Link>
              <Link to="/konto/login" data-testid="mobile-nav-login" onClick={() => setOpen(false)} className="text-sm font-medium text-slate-700">
                {t.nav.login}
              </Link>
              <Link
                to="/#contact"
                data-testid="mobile-nav-cta"
                onClick={() => setOpen(false)}
                className="inline-flex items-center px-5 py-2.5 text-xs font-mono-spec font-semibold bg-slate-950 text-white rounded-full"
              >
                {t.nav.cta}
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
